import React, { useRef } from 'react';
import { Card, CardHeader, CardBody, Button, Input } from '@nextui-org/react';
import { FiUser, FiBriefcase, FiMapPin, FiCamera, FiSave } from 'react-icons/fi';

function ProfileCardComponent({ user, form, onChange, onSave, avatarFile, onAvatarChange, onUploadAvatar, isUploading }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü
      if (file.size > 1024 * 1024) {
        alert('Dosya boyutu 1MB\'dan büyük olamaz');
        return;
      }
      // Dosya türü kontrolü
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Sadece JPG, JPEG ve PNG formatları desteklenir');
        return;
      }
      onAvatarChange(e);
    }
  };

  return (
    <Card className="border border-gray-100 dark:border-dark-400 bg-white dark:bg-dark-300">
      <CardHeader className="flex flex-col items-start gap-2 pb-4">
        <h2 className="text-xl font-semibold text-[#283252] dark:text-darkWhite-300">Genel Bilgiler</h2>
        <p className="text-sm text-gray-500 dark:text-darkWhite-200">
          Hesabınızın genel bilgilerini düzenleyebilirsiniz.
        </p>
      </CardHeader>
      <CardBody className="gap-6">
        {/* Avatar Yükleme Bölümü */}
        <div className="flex flex-col items-center gap-4 pb-6 border-b border-gray-200 dark:border-dark-300">
          <div className="relative">
            <div className="relative">
              <img
                src={
                  avatarFile
                    ? URL.createObjectURL(avatarFile)
                    : (user?.uimg
                      ? (/^https?:\/\//i.test(user.uimg)
                          ? user.uimg
                          : `${process.env.REACT_APP_USERIMG || ''}${user.uimg}.${user.uimgtype || 'png'}`)
                      : (process.env.REACT_APP_USERIMG_BLANK || '/assets/heypena-proffil.png'))
                }
                alt="Profil Fotoğrafı"
                className="w-32 h-32 rounded-full object-cover ring-2 ring-gray-200/60 dark:ring-dark-500/50"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-all duration-200 hover:scale-110"
              >
                <FiCamera className="w-4 h-4" />
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="text-center">
            <p className="text-sm font-medium text-[#283252] dark:text-darkWhite-300">Profil Fotoğrafınız</p>
            <p className="text-xs text-gray-500 dark:text-darkWhite-200 mt-1">
              Bu sizin tanınmanıza yardımcı olur. (Maks. 1MB, JPG/PNG)
            </p>
          </div>
          {avatarFile && (
            <Button
              size="sm"
              onClick={onUploadAvatar}
              disabled={isUploading}
              className="bg-blue-600 text-white"
              startContent={<FiCamera />}
            >
              {isUploading ? 'Yükleniyor...' : 'Avatarı Yükle'}
            </Button>
          )}
        </div>

        {/* Form Alanları */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Ad"
              placeholder="Adınızı girin"
              name="ufname"
              value={form.ufname ?? ''}
              onChange={onChange}
              isRequired
              variant="flat"
              classNames={{
                input: "text-[#283252] dark:text-darkWhite-300",
                label: "text-gray-700 dark:text-darkWhite-200",
                inputWrapper: "bg-transparent border border-gray-200/60 dark:border-dark-500/50 hover:border-gray-300/60 focus-within:border-blue-500/60"
              }}
              startContent={<FiUser className="text-gray-400" />}
            />
            <Input
              label="Soyad"
              placeholder="Soyadınızı girin"
              name="ulname"
              value={form.ulname ?? ''}
              onChange={onChange}
              variant="flat"
              classNames={{
                input: "text-[#283252] dark:text-darkWhite-300",
                label: "text-gray-700 dark:text-darkWhite-200",
                inputWrapper: "bg-transparent border border-gray-200/60 dark:border-dark-500/50 hover:border-gray-300/60 focus-within:border-blue-500/60"
              }}
              startContent={<FiUser className="text-gray-400" />}
            />
          </div>

          <Input
            label="Ünvan"
            placeholder="Ünvanınızı girin (örn: Yazılım Geliştirici)"
            name="uwork"
            value={form.uwork ?? ''}
            onChange={onChange}
            variant="flat"
            classNames={{
              input: "text-[#283252] dark:text-darkWhite-300",
              label: "text-gray-700 dark:text-darkWhite-200",
              inputWrapper: "bg-transparent border border-gray-200/60 dark:border-dark-500/50 hover:border-gray-300/60 focus-within:border-blue-500/60"
            }}
            startContent={<FiBriefcase className="text-gray-400" />}
          />

          <Input
            label="Konum"
            placeholder="Konumunuzu girin (örn: İstanbul, Türkiye)"
            name="ulocation"
            value={form.ulocation ?? ''}
            onChange={onChange}
            variant="flat"
            classNames={{
              input: "text-[#283252] dark:text-darkWhite-300",
              label: "text-gray-700 dark:text-darkWhite-200",
              inputWrapper: "bg-transparent border border-gray-200/60 dark:border-dark-500/50 hover:border-gray-300/60 focus-within:border-blue-500/60"
            }}
            startContent={<FiMapPin className="text-gray-400" />}
          />
        </div>

        {/* Kaydet Butonu */}
        <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-dark-400">
          <Button
            onClick={onSave}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
            startContent={<FiSave />}
            size="lg"
          >
            Değişiklikleri Kaydet
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default ProfileCardComponent;